export const ROUTES = {
  HOME: '/',
  SIGN_IN: '/sign-in',
  SIGN_IN_BANNER: '/sign-in-banner',
  // VERA-837: dedicated Forgot password page (previously handled inline
  // inside SignInBanner)
  FORGOT_PASSWORD: '/forgot-password',
  AUTH_CALLBACK: '/auth/callback',
  // VERA-1095: public self-signup flow (unauthenticated)
  SIGNUP: '/signup',
  SIGNUP_SUCCESS: '/signup/success',
  // VERA-843: Account Email Path — "create account" invite landing route
  CREATE_ACCOUNT: '/create-account',
  // VERA-839: Claim-Ownership Sign-Up — "claim ownership" invite landing route
  CLAIM: '/claim',
  CERTIFICATES: '/certificates',
  PREVIEW: '/preview',
  CERTIFICATE: '/certificates/:certificateId',
  /*
   * VRG-455 — a purchase the buyer has not claimed yet.
   *
   * NOT under `/certificates/:certificateId`, and it cannot be: no certificate
   * is bound until the scan resolves which physical unit they hold, so there is
   * no id to route on. That absence is the feature's premise, not a gap.
   *
   * Keyed on the pending-ownership record instead. The page it opens mirrors the
   * certificate page's SHAPE — photo, title, then state — so the buyer meets the
   * same layout they will keep after claiming, with the owner-only half absent
   * until it is theirs.
   */
  PENDING_SALE: '/purchases/:pendingSaleId',
  /* `CERTIFICATE_ACCESS: '/certificates/:id/access'` was HERE and is deliberately
     gone. The public access gate is a state card ON the certificate, not a page —
     see the note at its former `<Route>` in `App.tsx` for why, and for the two
     times it has been built as a page and reverted. Removing the constant as well
     as the route is the point: a name left behind is what made re-wiring it look
     like connecting something that already existed. */
  // VERA-298: owner-only Share screen for a certificate's shareable views.
  CERTIFICATE_SHARE: '/certificates/:certificateId/share',
  // VERA-315 Phase 4: the certificate's four tabs became inline Product content
  // plus chevron rows pushing to these. Owner-only, like Share. (It was "three rows"
  // until VRG-782 appended the brand's Experience row below — the count is no longer
  // fixed, which is the point of the section registry.)
  CERTIFICATE_PROVENANCE: '/certificates/:certificateId/provenance',
  CERTIFICATE_SCANS: '/certificates/:certificateId/scans',
  CERTIFICATE_ACTIVITY: '/certificates/:certificateId/activity',
  // VRG-782 — the brand's Experience copy, for the OWNER. A public visitor gets the
  // same content inline on the certificate instead (see ExperienceSubView's note), so
  // this route being owner-only is a layout fact, not a gate on the content.
  //
  // Registered here AND in App.tsx. The section registry
  // (`CertificatePage/certificateSections`) decides whether the ROW appears; it cannot
  // conjure the route, because a pushed screen needs an element and react-router has no
  // way to infer one. A brand section added to the registry with no route here renders a
  // row that navigates to a 404.
  CERTIFICATE_EXPERIENCE: '/certificates/:certificateId/experience',
  PROFILE: '/profile',
  // VERA-304: the signed-in user's OWN code — the QR others scan to address a
  // gift to them, plus the scanner for reading someone else's. Under /profile
  // because it is reached by pushing from there; distinct from PROFILE_CODE
  // below, which is the PUBLIC landing page for a code that has been scanned.
  PROFILE_MY_CODE: '/profile/code',
  // VERA-315: the Activity bottom-nav tab — a cross-certificate event feed.
  // Distinct from CERTIFICATE_ACTIVITY, which is one certificate's timeline.
  ACTIVITY: '/activity',
  // VERA-291: certificate-owner notification-preferences page. VERA-315 moved it
  // OUT of the Activity tab and under Profile, where the rest of the account
  // settings live; the path is unchanged so existing links keep working.
  NOTIFICATIONS: '/notifications',
  // VRG-418: the consumer Help surface — articles plus the consumer glossary.
  // PUBLIC (see App.tsx): the certificate route itself is public because a stranger who
  // taps a tag has no account, and that stranger is exactly who needs "what does verified
  // mean".
  HELP: '/help',
  HELP_ARTICLE: '/help/:slug',
  INVALID_SCANS: '/invalid-scan',
  // VRG-268: where a RETIRED tag lands (today, one that has been replaced). Public and
  // unauthenticated by necessity: it is reached by tapping a physical tag.
  TAG_REPLACED: '/tag-replaced',
  // VRG-275: where a HELD replacement tag lands (bound to a certificate but not yet
  // resolving, because it was mailed to the owner rather than fitted on the bench).
  // Public and unauthenticated by necessity, same as TAG_REPLACED — it doubles as a
  // claim flow: the page itself carries the "sign in to confirm receipt" step.
  TAG_NOT_ACTIVE: '/tag-not-active',
  // VRG-319: where a genuine tag lands when the CERTIFICATE it is bound to has never been
  // activated (or was deactivated back to `inactive`). Distinct from TAG_NOT_ACTIVE above,
  // which is about the TAG being held: the route name says which of the two is not active.
  // Public and unauthenticated by necessity, same as the other two. No action to offer, so
  // unlike TAG_NOT_ACTIVE it carries no certificateId.
  CERTIFICATE_NOT_ACTIVE: '/certificate-not-active',
  /*
   * Where an UNEXPECTED server error during `GET /verify/:global_tag_id` lands — the residual
   * case after the three routes above, which each cover a KNOWN outcome of a tap. Public and
   * unauthenticated by necessity, same as they are: it is reached by tapping a physical tag.
   *
   * Optional `?ref=<short id>` carries a correlation id the customer can quote and that
   * resolves in the server logs; the page renders it only when present. It replaces the
   * global exception filter's raw JSON, which iOS Safari served to real customers as a FILE
   * DOWNLOAD named after the tag id. See the page's header comment.
   */
  SOMETHING_WENT_WRONG: '/something-went-wrong',
  COMPANY_SUSPENDED: '/company-suspended',
  // VRG-358: where a profile QR resolves when it is read by a phone's own
  // camera rather than by the in-app scanner. Short on purpose — the code is
  // already 16 characters and the whole string has to survive being printed at
  // QR density. Public and unauthenticated by necessity: possession of the code
  // is the authorization, and the person scanning may not have an account yet.
  PROFILE_CODE: '/u/:code',
} as const;

export type Route = (typeof ROUTES)[keyof typeof ROUTES];
